import {
    ApplicationConfig,
    importProvidersFrom,
    inject,
    LOCALE_ID,
    provideAppInitializer,
    provideBrowserGlobalErrorListeners,
    provideZoneChangeDetection
} from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { tokenInterceptor } from '@core/interceptors/token-interceptor';
import { TokenService } from '@core/services/token.service';
import { AuthService } from '@core/services/api/auth.service';
import { catchError, forkJoin, of } from 'rxjs';
import { languageInterceptor } from '@core/interceptors/language-interceptor';
import { errorInterceptor } from '@core/interceptors/error-interceptor';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import localeFr from '@angular/common/locales/fr';
import localeEn from '@angular/common/locales/en';
import localeNl from '@angular/common/locales/nl';
import localeDE from '@angular/common/locales/de';
import localeFrBe from '@angular/common/locales/fr-BE';

import { registerLocaleData } from '@angular/common';
import { provideFlatpickrDefaults } from 'angularx-flatpickr';

import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFilePoster from 'filepond-plugin-file-poster';
import { registerPlugin } from 'filepond';
import { GoogleMapsModule } from '@angular/google-maps';
import { SoftwareService } from '@core/services/api/software.service';
import { installDecimalInputListener } from '@core/utils/decimal-input-listener';

registerLocaleData(localeFr);
registerLocaleData(localeEn);
registerLocaleData(localeNl);
registerLocaleData(localeFrBe);
registerLocaleData(localeDE);

registerPlugin(
    FilePondPluginFileValidateSize,
    FilePondPluginFileValidateType,
    FilePondPluginImagePreview,
    FilePondPluginFilePoster,
)

export function initAuth(tokenService: TokenService, authService: AuthService, softwareService: SoftwareService) {
    return () => {
        const auth$ = tokenService.getToken()
            ? authService.me().pipe(catchError(() => of(null)))
            : of(null);

        const software$ = softwareService.init().pipe(
            catchError(() => of(null))
        );

        return forkJoin([software$, auth$]);
    };
}

const randomString = () => Math.random().toString(36).substring(2, 6) + Math.random().toString(36).substring(2, 6);

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideZoneChangeDetection({eventCoalescing: true}),
        provideRouter(routes),
        provideHttpClient(
            withInterceptors([tokenInterceptor, languageInterceptor, errorInterceptor]),
        ),
        provideAppInitializer(() => {
            installDecimalInputListener();
            const initializerFn = initAuth(inject(TokenService), inject(AuthService), inject(SoftwareService));
            return initializerFn();
        }),
        importProvidersFrom(GoogleMapsModule),
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: '/i18n/',
                suffix: '.json?v=' + randomString(),
            }),
            fallbackLang: 'en',
            lang: localStorage.getItem('__GOHY__LANG__') || 'fr'
        }),
        { provide: LOCALE_ID, useValue: 'fr-BE' },
        provideFlatpickrDefaults(),
    ]
};
